import { Component, ElementRef, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, tap, timeout } from 'rxjs';
import { Audit } from 'src/app/core-module/models/Audits/audit';
import { AuditCollectionResult } from 'src/app/core-module/models/Audits/audit-collection-result';
import { AuditCompletionInfo } from 'src/app/core-module/models/Audits/audit-completion-info';
import { AuditResultDetail } from 'src/app/core-module/models/Audits/AuditResults/audit-result-detail';
import { AuditResultFilters } from 'src/app/core-module/models/Audits/AuditResults/audit-result-filters';
import { AuditSummaryFollowupRequest } from 'src/app/core-module/models/Audits/AuditResults/audit-summary-followup-request';
import { AuditSummaryResult } from 'src/app/core-module/models/Audits/AuditResults/audit-summary-result';
import { AuditSummaryResultRequest } from 'src/app/core-module/models/Audits/AuditResults/audit-summary-result-request';
import { BasicAuditReturn } from 'src/app/core-module/models/Audits/AuditResults/basic-audit-return';
import { PagedAuditResultList } from 'src/app/core-module/models/Audits/AuditResults/paged-audit-result-list';
import { ResultFiltersReturn } from 'src/app/core-module/models/Audits/AuditResults/result-filters-return';
import { Campaign } from 'src/app/core-module/models/Campaigns/campaign';
import { CampaignListResult } from 'src/app/core-module/models/Campaigns/campaign-list-result';
import { CampaignReturn } from 'src/app/core-module/models/Campaigns/campaign-return';
import { CompletedCampaignResult } from 'src/app/core-module/models/Campaigns/completed-campaign-result';
import { DDL } from 'src/app/core-module/models/ddl';
import { ExportJobResult } from 'src/app/core-module/models/Exports/export-job-result';
import { AuditLocation } from 'src/app/core-module/models/Locations/audit-location';
import { LocationResult } from 'src/app/core-module/models/Locations/location-result';
import { TasksResult } from 'src/app/core-module/models/Tasks/TasksResult';
import { ProgramFeatures } from 'src/app/core-module/models/UserManagement/program-features';
import { UserSettingsPermissionsResult } from 'src/app/core-module/models/UserManagement/user-settings-permissions-result';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { AuditRequestsService } from 'src/app/core-module/services/audit-requests.service';
import { AuditResultsService, AuditSummaryStreamEvent } from 'src/app/core-module/services/audit-results.service';
import { AuditService } from 'src/app/core-module/services/audit.service';
import { AuthService } from 'src/app/core-module/services/auth.service';
import { ExportService } from 'src/app/core-module/services/export.service';
import { ReferenceService } from 'src/app/core-module/services/reference.service';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';
import { UtilService } from 'src/app/core-module/services/util.service';
import { CustomerContextService } from 'src/app/core-module/services/customer-context.service';

@Component({
  selector: 'app-results-home',
  templateUrl: './results-home.component.html',
  styleUrl: './results-home.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ResultsHomeComponent {
  private readonly customerContext = inject(CustomerContextService);

  public auditResultList: Audit[] = [];
  public auditResultDetail: AuditResultDetail = new AuditResultDetail;
  public startDate: Date = new Date();
  public endDate: Date = new Date();

  public returnedLocations: AuditLocation[] = [];

  public selectedLocationDDL: DDL = new DDL();

  public returnedAudits: AuditCollectionResult = new AuditCollectionResult;
  public auditDDLList: DDL[] = [];
  public selectedAuditDDL: DDL = new DDL();

  public showFilters: boolean = true;
  public showList: boolean = true;
  public showResults: boolean = false;
  public showAISummary: boolean = false;
  public showActions: boolean = false;

  public isLoading: boolean = false;
  public isSummarising: boolean = false;
  public canSummarise: boolean = false;
  public readonly maxAISummaryAudits: number = 7000;
  public readonly largeAISummaryAuditThreshold: number = 200;
  public summarisedSinceLastSearch: boolean = false;
  public summaryResults: string = "";
  public hasAISummaryResponse: boolean = false;
  public aiSummaryRetryAvailable: boolean = false;
  public aiSummaryRetryButtonLabel: string = "Retry AI Summary";
  public aiSummaryRetryCountdown: number = 0;
  public aiSummaryStartNewChatAvailable: boolean = false;
  public prompt: string = "";
  public aiSummaryMode: 'quick' | 'deep' | '' = '';
  public aiSummaryStatusMessage: string = "";
  public aiSummaryHasStreamedText: boolean = false;

  public currentRequest: AuditSummaryResultRequest = new AuditSummaryResultRequest();
  public hasMadeRequest: boolean = false;
  public isExporting: boolean = false;
  public exportStatus: string = "";
  public exportProgress: number = 0;
  public exportDialogTitle: string = "";
  public exportDialogMessage: string = "";
  public exportProgressMode: 'determinate' | 'indeterminate' = 'indeterminate';
  private exportEstimateTimer: ReturnType<typeof setInterval> | null = null;

  public totalAuditResults: number = 0;
  public dueActions: number = 0;
  public overdueActions: number = 0;
  public completedActions: number = 0;
  public totalResultCount: number = 0;
  public pageNumber: number = 1;
  public pageSize: number = 100;
  public firstResult: number = 0;
  public pageSizeOptions: number[] = [25, 50, 100];

  public rawSummaryResult: AuditSummaryResult;
  private aiSummaryRetryMode: 'summary' | 'quick-summary' | 'followup' | '' = '';
  private lastFailedFollowUpPrompt: string = "";
  private quickSummaryContextForDeepDive: string = "";
  private aiSummaryRetryTimer: ReturnType<typeof setInterval> | null = null;
  private readonly aiSummaryClientTimeoutMs: number = 10 * 60 * 1000;

  public IncludeSubLocations: boolean = false;
  public IncludeCampaignSubLocations: boolean = false;

  public validSummary: boolean = false;

  public showAdditionalOptions: boolean = false;

  public selectedAuditType: DDL = new DDL();
  public auditTypes: DDL[] = [];
  public selectedFrequency: DDL = new DDL();
  public frequencies: DDL[] = [];

  private filterReturn: ResultFiltersReturn = new ResultFiltersReturn();

  private returnedAuditList: BasicAuditReturn[] = [];
  public displayAudits: BasicAuditReturn[] = []
  public selectedAudit: BasicAuditReturn = new BasicAuditReturn();
  public displayCampaignAudits: BasicAuditReturn[] = [];
  public selectedCampaignAudit: BasicAuditReturn = new BasicAuditReturn();
  public selectedLocation: AuditLocation = new AuditLocation();
  public locations: AuditLocation[] = [];
  public locationGroups: { label: string; items: AuditLocation[] }[] = [];
  public locationGroupsWithAll: { label: string; items: AuditLocation[] }[] = [];
  public useLocationGroups: boolean = false;
  public selectedCampaignLocation: DDL = new DDL();
  public campaignLocations: DDL[] = [];
  public campaignLocationGroups: { label: string; items: DDL[] }[] = [];
  public campaignLocationGroupsWithAll: { label: string; items: DDL[] }[] = [];
  public useCampaignLocationGroups: boolean = false;
  public selectedCampaign: Campaign = new Campaign();
  public campaigns: Campaign[] = [];

  public campaignMode: boolean = false;
  public campaignResultLoading: boolean = false;
  public campaignAuditResultView: boolean = false;
  public campaignAuditDetailView: boolean = false;


  public validResultReturn: boolean = false;
  public validCampaignReturn: boolean = false;

  public showAITips: boolean = true;


  private hasCustomerCampaign: boolean = false;
  public hasCampaignPermission: boolean = false;

  public showKey: boolean = false;
  public openedFromDashboard: boolean = false;

  public campaignResultReturn: CampaignListResult = new CampaignListResult();

  public completedCampaign: CompletedCampaignResult = new CompletedCampaignResult();

  public campaignResultDetail: CompletedCampaignResult = new CompletedCampaignResult();

  @ViewChild('actionsPanel', { static: false }) actionsPanel!: ElementRef<HTMLElement>;

  get canGenerateAISummary(): boolean {
    return this.canSummarise && this.totalAuditResults <= this.maxAISummaryAudits;
  }

  get showLongAISummaryMessage(): boolean {
    return this.totalAuditResults > this.largeAISummaryAuditThreshold;
  }

  get aiSummaryLoadingMessage(): string {
    if (this.aiSummaryStatusMessage) {
      return this.aiSummaryStatusMessage;
    }

    if (!this.showLongAISummaryMessage) {
      return "";
    }

    return `This summary includes ${this.totalAuditResults.toLocaleString()} audits, so deeper analysis may take longer.`;
  }

  get showAISummaryLoadingIndicator(): boolean {
    return this.isSummarising && !this.aiSummaryHasStreamedText;
  }

  constructor(private auditResults: AuditResultsService, private auditRequestService: AuditRequestsService, private refService: ReferenceService, private auditService: AuditService, private exportService: ExportService, private toastr: ToastrService, private utilService: UtilService, private auth: AuthService, private userManagementService: UserManagementService, private route: ActivatedRoute, private router: Router) {
    this.getFeaturePermissions();

    this.getInitialFilters();
    this.startDate = new Date(new Date().setDate(this.startDate.getDate() - 7));
    this.getAuditList();
    this.getValidLocation();
    this.getAuditFilter();

    this.route.queryParams.subscribe(params => {
      const auditResultId = Number(params['auditResultId'] ?? -1);
      this.openedFromDashboard = params['from'] === 'dashboard';
      if (auditResultId && auditResultId > 0){
        this.getAudit(auditResultId);
      }
    });
  }
  getFeaturePermissions() {
    this.auth.getFeaturePermissions().pipe(
      tap((res: ProgramFeatures) => {
        if (!res.IsError) {
          this.hasCustomerCampaign = res.Campaigns;
          if (res.AIFeatures) this.validSummary = true;
          this.userCampaignCheck();
        }
      })
    ).subscribe();
  }
  userCampaignCheck() {
    this.userManagementService.userCampaignPermissions().pipe(
      tap((res: UserSettingsPermissionsResult) => {
        if (!res.IsError) {
          if (this.hasCustomerCampaign && res.ShowCampaigns)
            this.hasCampaignPermission = true;
        } else {
          this.hasCampaignPermission = false;
        }

      })).subscribe()
  }
  updateAudits() {
    this.displayAudits = [];

    this.returnedAuditList.forEach(e => {
      if ((e.AuditFrequency.ID == this.selectedFrequency.ID || this.selectedFrequency.ID == -1) && (e.AuditType.ID == this.selectedAuditType.ID || this.selectedAuditType.ID == -1)) {
        this.displayAudits.push(new BasicAuditReturn(e));
      }
    })
    this.displayAudits.unshift(new BasicAuditReturn({ AuditID: -1, AuditName: this.customerContext.displayText('All Audits') }));
    this.selectedAudit = this.displayAudits[0];
  }
  getInitialFilters() {
    this.auditResults.getAuditResultFilters().pipe(
      tap((res: ResultFiltersReturn) => {
        if (!res.IsError) {
          this.filterReturn = res;
          this.populateFilters(res.Filters);
        }
      }

      )
    ).subscribe();
  }
  populateFilters(r: AuditResultFilters) {
    this.auditTypes = r.AuditTypes;
    this.frequencies = r.Frequencies;
    this.returnedAuditList = r.Audits;
    this.returnedLocations = r.Locations;
    this.buildLocations();
    this.buildAuditFilter();
    this.buildAuditTypes();
    this.buildFrequencies();
    this.buildCampaigns();
    this.buildCampaignAudits();
  }
  showCampaign(showCampaign: boolean) {
    if (this.campaignMode != showCampaign) {
      if (this.campaignMode) {
        //load audit options
      } else {
        //load campaign options
      }
      this.campaignMode = showCampaign;
    }

  }
  buildLocations() {
    this.locations = [];
    const allLocations = new AuditLocation({ LocationID: -1, LocationName: "All Locations" });
    const buildResult = this.buildResultLocations(this.returnedLocations || []);
    this.locations = [allLocations, ...buildResult.locations];
    this.locationGroups = buildResult.groups;
    this.useLocationGroups = buildResult.useGroups;
    this.locationGroupsWithAll = this.useLocationGroups
      ? [{ label: '', items: [allLocations] }, ...this.locationGroups]
      : [];
    this.selectedLocation = this.locations[0];
  }

  private buildResultLocations(locations: AuditLocation[]): {
    locations: AuditLocation[];
    groups: { label: string; items: AuditLocation[] }[];
    useGroups: boolean;
  } {
    if (!locations || locations.length === 0) {
      return { locations: [], groups: [], useGroups: false };
    }
    const normalized = locations.map(l => {
      const loc = new AuditLocation(l);
      if (!loc.LocationLevelSeq || loc.LocationLevelSeq === -1) {
        loc.LocationLevelSeq = 1;
      }
      return loc;
    });
    const nameCompare = (a: AuditLocation, b: AuditLocation) =>
      a.LocationName.localeCompare(b.LocationName, undefined, { sensitivity: 'base' });
    const levelSeqs = Array.from(new Set(normalized.map(l => l.LocationLevelSeq))).sort((a, b) => a - b);
    if (levelSeqs.length <= 1) {
      return {
        locations: normalized.sort(nameCompare),
        groups: [],
        useGroups: false
      };
    }
    const levelMap = new Map<number, number>();
    levelSeqs.forEach((seq, index) => {
      levelMap.set(seq, index + 1);
    });
    normalized.forEach(l => {
      l.LocationLevelDisplay = levelMap.get(l.LocationLevelSeq) ?? 1;
    });
    const sorted = normalized.sort((a, b) => {
      const levelDiff = (a.LocationLevelDisplay ?? 1) - (b.LocationLevelDisplay ?? 1);
      if (levelDiff !== 0) {
        return levelDiff;
      }
      return nameCompare(a, b);
    });
    const groups: { label: string; items: AuditLocation[] }[] = [];
    let currentLevel = -1;
    let currentGroup: { label: string; items: AuditLocation[] } | null = null;
    for (const loc of sorted) {
      const displayLevel = loc.LocationLevelDisplay ?? 1;
      if (displayLevel !== currentLevel) {
        currentGroup = { label: `Level ${displayLevel}`, items: [] };
        groups.push(currentGroup);
        currentLevel = displayLevel;
      }
      currentGroup?.items.push(loc);
    }
    return {
      locations: normalized,
      groups,
      useGroups: true
    };
  }
  buildAuditTypes() {
    this.auditTypes = [];
    this.auditTypes.push(new DDL({ ID: -1, Description: this.customerContext.displayText("All Audit Types") }));
    this.filterReturn.Filters.AuditTypes.forEach(e => {
      this.auditTypes.push(new DDL(e));
    })
    this.selectedAuditType = this.auditTypes[0];
  }
  buildFrequencies() {
    this.frequencies = [];
    this.frequencies.push(new DDL({ ID: -1, Description: this.customerContext.displayText("All Audit Frequencies") }));
    this.filterReturn.Filters.Frequencies.forEach(e => {
      this.frequencies.push(new DDL(e));
    })
    this.selectedFrequency = this.frequencies[0];

  }
  buildCampaigns() {

    this.campaigns = [];
    this.campaignLocations = [];

    this.campaignLocations.push(new DDL({ ID: -1, Description: "All Locations" }))
    this.filterReturn.Filters.Campaigns.forEach(e => {
      this.campaigns.push(new Campaign(e));
      if (this.campaignLocations.findIndex(f => f.ID == e.Location.ID) == -1) {
        this.campaignLocations.push(e.Location);
      }
    })
    const buildResult = this.buildCampaignLocationGroups(this.campaignLocations.slice(1));
    const allLocations = this.campaignLocations[0];
    this.campaignLocations = [allLocations, ...buildResult.locations];
    this.campaignLocationGroups = buildResult.groups;
    this.useCampaignLocationGroups = buildResult.useGroups;
    this.campaignLocationGroupsWithAll = this.useCampaignLocationGroups
      ? [{ label: '', items: [allLocations] }, ...this.campaignLocationGroups]
      : [];
    this.campaigns.unshift(new Campaign({ ID: -1, RequestName: "All Feedback Campaigns" }));
    this.selectedCampaign = this.campaigns[0];
    this.selectedCampaignLocation = this.campaignLocations[0];
  }

  private buildCampaignLocationGroups(locations: DDL[]): {
    locations: DDL[];
    groups: { label: string; items: DDL[] }[];
    useGroups: boolean;
  } {
    if (!locations || locations.length === 0) {
      return { locations: [], groups: [], useGroups: false };
    }
    const normalized = locations.map(l => new DDL(l));
    const nameCompare = (a: DDL, b: DDL) =>
      a.Description.localeCompare(b.Description, undefined, { sensitivity: 'base' });
    const levelSeqs = Array.from(new Set(normalized.map(l => (l as any).LocationLevelSeq).filter(v => v)))
      .sort((a, b) => a - b);
    if (levelSeqs.length <= 1) {
      return {
        locations: normalized.sort(nameCompare),
        groups: [],
        useGroups: false
      };
    }
    const levelMap = new Map<number, number>();
    levelSeqs.forEach((seq, index) => {
      levelMap.set(seq, index + 1);
    });
    normalized.forEach(l => {
      const seq = (l as any).LocationLevelSeq;
      (l as any).LocationLevelDisplay = levelMap.get(seq) ?? 1;
    });
    const sorted = normalized.sort((a, b) => {
      const levelDiff = ((a as any).LocationLevelDisplay ?? 1) - ((b as any).LocationLevelDisplay ?? 1);
      if (levelDiff !== 0) {
        return levelDiff;
      }
      return nameCompare(a, b);
    });
    const groups: { label: string; items: DDL[] }[] = [];
    let currentLevel = -1;
    let currentGroup: { label: string; items: DDL[] } | null = null;
    for (const loc of sorted) {
      const displayLevel = (loc as any).LocationLevelDisplay ?? 1;
      if (displayLevel !== currentLevel) {
        currentGroup = { label: `Level ${displayLevel}`, items: [] };
        groups.push(currentGroup);
        currentLevel = displayLevel;
      }
      currentGroup?.items.push(loc);
    }
    return {
      locations: normalized,
      groups,
      useGroups: true
    };
  }
  buildCampaignAudits() {
    this.displayCampaignAudits = [];

    this.filterReturn.Filters.CampaignAudits.forEach(e => {
      this.displayCampaignAudits.push(new BasicAuditReturn(e));
    })
    this.displayCampaignAudits.unshift(new BasicAuditReturn({ AuditID: -1, AuditName: this.customerContext.displayText('All Audits') }));
    this.selectedCampaignAudit = this.displayCampaignAudits[0];
  }

  getValidLocation() {
    this.refService.getFlatLocationsForUser().pipe(
      tap((res: AuditLocation[]) => {
        if (res != null) {
          this.returnedLocations = res
          this.buildLocations();
        }
      })
    ).subscribe();
  }
  getAuditFilter() {
    this.auditService.getAudits().pipe(
      tap((res: AuditCollectionResult) => {
        if (res != null) {
          this.returnedAudits = res
          this.buildAuditFilter();
        }
      })
    ).subscribe();
  }
  buildAuditFilter() {
    this.displayAudits = [];

    this.returnedAuditList.forEach(e => {
      this.displayAudits.push(new BasicAuditReturn(e));
    })
    this.displayAudits.unshift(new BasicAuditReturn({ AuditID: -1, AuditName: this.customerContext.displayText('All Audits') }));
    this.selectedAudit = this.displayAudits[0];
  }
  getAuditList() {
    this.canSummarise = false;
    this.removeStartDateTimeZone(this.startDate);
    this.removeEndDateTimeZone(this.endDate);
    var auditResultRequest = new AuditSummaryResultRequest({ hasStart: true, StartDate: this.startDate, hasEnd: true, EndDate: this.endDate, IncludeSubLocations: this.IncludeSubLocations, isCampaign: this.campaignMode, ExcludeEventResults: !this.campaignMode && this.selectedAudit.AuditID == -1, AuditTypeID: this.selectedAuditType.ID, AuditFrequencyID: this.selectedFrequency.ID, PageNumber: this.pageNumber, PageSize: this.pageSize });
    if (this.selectedAudit.AuditID != -1) {
      auditResultRequest.AuditID = this.selectedAudit.AuditID!
    }
    if (this.selectedLocation.LocationID != -1) {
      auditResultRequest.LocationID = this.selectedLocation.LocationID!
    }
    this.currentRequest = auditResultRequest;
    this.currentRequest.AuditName = this.selectedAuditDDL.Description;
    this.currentRequest.LocationName = this.selectedLocation.LocationName;
    if (this.currentRequest.AuditName == undefined) {
      this.currentRequest.AuditName = this.customerContext.displayText('All Audits');
    }
    if (this.currentRequest.LocationName == undefined) {
      this.currentRequest.LocationName = 'All Locations';
    }

    if (this.campaignMode) {
      auditResultRequest.CampaignID = this.selectedCampaign.AuditRequestID;
      auditResultRequest.LocationID = this.selectedCampaignLocation.ID;
      auditResultRequest.AuditID = this.selectedCampaignAudit.AuditID;
      this.currentRequest.AuditName = this.selectedCampaignAudit.AuditName ?? this.customerContext.displayText('All Audits');
      if (this.selectedCampaignLocation.ID == -1) {
        this.currentRequest.LocationName = 'All Locations';
      } else {
        this.currentRequest.LocationName = this.selectedCampaignLocation.Description;
      }
    }
    this.hasMadeRequest = true;

    if (this.campaignMode) {
      this.campaignResultReturn = new CampaignListResult();
      this.validCampaignReturn = false;
      this.totalAuditResults = 0;
      this.auditRequestService.getCampaignResults(auditResultRequest).pipe(tap((res: CampaignListResult) => {
        if (!res.IsError) {
          this.campaignResultReturn = new CampaignListResult(res);
          this.totalAuditResults = this.campaignResultReturn.Campaigns.reduce((total, campaign) => total + Math.max(campaign.TotalSubmitted ?? 0, 0), 0);
          this.canSummarise = this.totalAuditResults > 0;
          if (this.campaignResultReturn.Campaigns.length > 0) {
            this.validCampaignReturn = true;
          }
        }
        this.isLoading = false;

      })).subscribe()

    } else {
      this.auditResults.getPagedAuditResultList(auditResultRequest).pipe(
        tap((res: PagedAuditResultList) => {
          if (res != null) {
            this.auditResultList = res.Results;
            this.totalResultCount = res.TotalCount;
            this.totalAuditResults = res.Summary.TotalAudits;
            this.dueActions = res.Summary.OutstandingActions;
            this.overdueActions = res.Summary.OverdueActions;
            this.completedActions = res.Summary.CompletedActions;
            this.validResultReturn = this.totalResultCount > 0;
          } else {
            this.validResultReturn = false;
            this.totalResultCount = 0;
            this.updateTotal();
            this.toastr.addToastr(new Toastr({ Type: 3, Message: "Unable to return results, please try again." }));
          }
          this.isLoading = false;
          this.canSummarise = true;
        })
      ).subscribe();

    }

  }
  updateTotal() {
    this.totalAuditResults = 0;
    this.dueActions = 0;
    this.overdueActions = 0;
    this.completedActions = 0;

    if (this.auditResultList.length > 0) {
      this.totalAuditResults = this.auditResultList.length
      this.auditResultList.forEach(e => {
        this.overdueActions += e.AuditCompletionInfo.TotalOverdueActions;
        this.dueActions += e.AuditCompletionInfo.TotalOutstandingActions;
        this.completedActions += (e.AuditCompletionInfo.TotalActionsRaised - e.AuditCompletionInfo.TotalOutstandingActions);
      })
    }
  }
  getAudit(AuditResultID: any) {
    this.auditResultDetail.AuditName = "Loading...";
    this.auditResults.getAuditResultDetail(AuditResultID).pipe(
      tap((res:AuditResultDetail) => {
        if (res != null) {
          this.auditResultDetail = res;
          document.getElementById("scroller")!.scrollTop = 0;
        }
      })
    ).subscribe();
    this.showDetail();
    //change panel
  }
  showActionPanel() {
    this.showResults = false;
    this.showActions = true;
  }
  hideActionPanel() {
    this.showActions = false;
    this.showResults = true;
  }
  showDetail() {
    this.showList = false;
    this.showResults = true;
    this.showAISummary = false;
  }

  resultBackLabel(): string {
    return this.openedFromDashboard ? 'Back to dashboard' : 'Back to results';
  }

  returnFromResultDetail(): void {
    if (this.openedFromDashboard) {
      this.router.navigate(['/dashboards']);
      return;
    }

    this.unhideList();
  }

  unhideFilters() {
    this.showFilters = true;
  }
  unhideList() {
    this.showList = true;
    this.showResults = false;
    this.showAISummary = false;
  }
  unhideCampaignList() {
    this.showList = true;
    this.campaignAuditResultView = false
  }
  unhideCampaignDetailList() {
    this.campaignAuditResultView = true;
    this.campaignAuditDetailView = false;
  }
  refreshSearch() {
    this.isLoading = true;
    this.pageNumber = 1;
    this.firstResult = 0;
    this.getAuditList();
    this.summarisedSinceLastSearch = false;
    this.aiSummaryMode = '';
    this.quickSummaryContextForDeepDive = '';
    //this.showFilters =false;
  }
  onResultsPageChange(event: any) {
    this.firstResult = event.first || 0;
    this.pageSize = event.rows || this.pageSize;
    this.pageNumber = Math.floor(this.firstResult / this.pageSize) + 1;
    this.isLoading = true;
    this.getAuditList();
  }
  exportSelection() {
    if (this.hasMadeRequest) {
      this.isExporting = true;
      this.exportDialogTitle = this.customerContext.displayText("Exporting audit results");
      this.exportDialogMessage = this.customerContext.displayText(`Preparing ${this.totalResultCount.toLocaleString()} audit results.`);
      this.exportStatus = this.customerContext.displayText("Estimating progress from the number of audits in this result set.");
      this.exportProgress = 0;
      this.exportProgressMode = 'determinate';
      this.startEstimatedExportProgress(this.totalResultCount);
      this.exportService.exportAuditResults(this.currentRequest).pipe(
        finalize(() => this.resetExportState())
      ).subscribe({
        error: () => this.toastr.addToastr(new Toastr({ Type: 3, Message: "Unable to export results, please try again." }))
      });
    }

  }

  exportFullAudit() {
    this.exportService.exportFullAudit(this.auditResultDetail).subscribe();
  }
  printAudit() {
    window.print();
  }
  generateQuickSummary() {
    if (this.isSummarising) return;
    if (!this.canGenerateAISummary) return;
    if (this.summarisedSinceLastSearch && this.aiSummaryMode == 'quick') {
      this.showAISummary = true;
      this.showList = false;
      this.showResults = false;
      return;
    }

    this.resetAISummaryFailureState();
    this.isSummarising = true;
    this.aiSummaryHasStreamedText = false;
    this.aiSummaryMode = 'quick';

    this.summaryResults = '';
    this.hasAISummaryResponse = false;

    this.showAISummary = true;
    this.showList = false;
    this.showResults = false;

    this.auditResults.generateQuickSummary(this.currentRequest).pipe(
      timeout(this.aiSummaryClientTimeoutMs),
      tap((res: AuditSummaryResult) => {
        this.rawSummaryResult = res;
        if (this.isBlankInitialAISummaryResponse(res)) {
          this.applyAISummaryFailure(new AuditSummaryResult({
            IsError: true,
            ErrorCode: 'AI_EMPTY_RESPONSE',
            ErrorMessage: 'The AI service is busy. Please retry shortly.',
            Retryable: true,
            RetryAfterSeconds: 30
          }), false, 'quick-summary');
        } else if (res.IsError) {
          this.applyAISummaryFailure(res, false, 'quick-summary');
        } else {
          const cleanedResponse = this.cleanAIResponseForDisplay(res.Response);
          res.Response = cleanedResponse;
          this.summaryResults = cleanedResponse;
          this.quickSummaryContextForDeepDive = cleanedResponse;
          this.hasAISummaryResponse = true;
          this.resetAISummaryFailureState();
          this.summarisedSinceLastSearch = true;
        }
      }),
      finalize(() => this.completeAISummaryRequest())
    ).subscribe({
      error: () => {
        this.applyAISummaryFailure(new AuditSummaryResult({
          IsError: true,
          ErrorCode: 'AI_TIMEOUT',
          ErrorMessage: 'The summary took too long to complete. Please try again.',
          Retryable: true
        }), false, 'quick-summary');
      }
    });
  }

  generateSummary() {
    if (this.isSummarising) return;
    if (!this.canGenerateAISummary) return;
    if (this.summarisedSinceLastSearch && this.aiSummaryMode == 'deep') {
      this.showAISummary = true;
      this.showList = false;
      this.showResults = false;
      return;
    }

    this.resetAISummaryFailureState();
    this.isSummarising = true;
    this.aiSummaryHasStreamedText = false;
    this.aiSummaryStatusMessage = "Preparing data";
    const quickSummaryContext = this.quickSummaryContextForDeepDive || (this.aiSummaryMode == 'quick' ? this.summaryResults : '');
    const deepDivePrompt = this.prompt;
    const deepDiveRequest = new AuditSummaryResultRequest({
      ...this.currentRequest,
      QuickSummaryContext: quickSummaryContext,
      DeepDivePrompt: deepDivePrompt
    });
    const existingQuickSummary = this.cleanAIResponseForDisplay(quickSummaryContext);
    this.aiSummaryMode = 'deep';

    const deepDivePrefix = existingQuickSummary
      ? existingQuickSummary + '<h3 class="ai-heading">Deep dive analysis</h3>' + (deepDivePrompt ? '<p class="ai-prompt">' + this.escapeHTML(deepDivePrompt) + '</p>' : '')
      : '';

    this.summaryResults = deepDivePrefix;
    this.hasAISummaryResponse = false;

    this.showAISummary = true;
    this.showList = false;
    this.showResults = false;

    let streamedSummary = '';

    this.auditResults.generateSummaryStream(deepDiveRequest).pipe(
      timeout(this.aiSummaryClientTimeoutMs),
      tap((event: AuditSummaryStreamEvent) => {
        if (event.type == 'status') {
          this.aiSummaryStatusMessage = event.message;
          return;
        }

        if (event.type == 'delta') {
          streamedSummary += event.text;
          const cleanedStreamedSummary = this.cleanAIResponseForDisplay(streamedSummary);
          this.summaryResults = deepDivePrefix + cleanedStreamedSummary;
          this.aiSummaryHasStreamedText = cleanedStreamedSummary.trim() != '';
          return;
        }

        if (event.type == 'error') {
          this.summaryResults = deepDivePrefix;
          this.applyAISummaryFailure(event.result, deepDivePrefix != '', 'summary');
          this.prompt = '';
          return;
        }

        const res = new AuditSummaryResult(event.result);
        res.Response = this.cleanAIResponseForDisplay(res.Response);
        this.rawSummaryResult = res;
        if (this.isBlankInitialAISummaryResponse(res)) {
          this.summaryResults = deepDivePrefix;
          this.applyAISummaryFailure(new AuditSummaryResult({
            IsError: true,
            ErrorCode: 'AI_EMPTY_RESPONSE',
            ErrorMessage: 'The AI service is busy. Please retry shortly.',
            Retryable: true,
            RetryAfterSeconds: 30
          }), deepDivePrefix != '', 'summary');
        } else if (res.IsError) {
          this.summaryResults = deepDivePrefix;
          this.applyAISummaryFailure(res, deepDivePrefix != '', 'summary');
        } else {
          if (streamedSummary.trim() == '' && res.Response) {
            this.summaryResults = deepDivePrefix + res.Response;
          }

          this.hasAISummaryResponse = true;
          this.resetAISummaryFailureState();
          this.summarisedSinceLastSearch = true;
          this.prompt = '';
        }
      }),
      finalize(() => this.completeAISummaryRequest())
    ).subscribe({
      error: () => {
        this.summaryResults = deepDivePrefix;
        this.applyAISummaryFailure(new AuditSummaryResult({
          IsError: true,
          ErrorCode: 'AI_TIMEOUT',
          ErrorMessage: 'The summary took too long to complete. Please try again.',
          Retryable: true
        }), deepDivePrefix != '', 'summary');
      }
    });
  }

  retryAISummary() {
    this.retryAIRequest();
  }

  displayDate(date: Date) {
    return this.utilService.humanDate(date);
  }

  generateFollowUp(questionOverride: string = "", appendPrompt: boolean = true) {
    if (this.isSummarising) return;
    if (!this.hasAISummaryResponse) return;
    const question = questionOverride || this.prompt;
    if (question == '') {
      this.toastr.addToastr(new Toastr({ Type: 3, Message: "Please add a question to ask.", Duration: 3000 }));
      return;
    }

    this.resetAISummaryFailureState();
    this.isSummarising = true;
    this.aiSummaryHasStreamedText = false;

    let followup: AuditSummaryFollowupRequest = new AuditSummaryFollowupRequest({
      FileID: this.rawSummaryResult.FileID,
      FileName: this.rawSummaryResult.FileName,
      AssistantID: this.rawSummaryResult.AssistantID,
      Question: question
    });

    if (appendPrompt) {
      this.summaryResults += '<p class="ai-prompt">' + this.escapeHTML(question) + '</p>';
    }

    this.auditResults.generateFollowUpRequest(followup).pipe(
      timeout(this.aiSummaryClientTimeoutMs),
      tap((res: AuditSummaryResult) => {
        if (res.IsError || !res.Response || res.Response.trim() == '') {
          const failure = res.IsError ? res : new AuditSummaryResult({
            IsError: true,
            ErrorCode: 'AI_EMPTY_RESPONSE',
            ErrorMessage: 'The AI service is busy. Please retry shortly.',
            Retryable: true,
            RetryAfterSeconds: 30
          });
          this.lastFailedFollowUpPrompt = question;
          this.applyAISummaryFailure(failure, true, 'followup');
        } else {
          res.Response = this.cleanAIResponseForDisplay(res.Response);
          this.summaryResults = this.cleanAIResponseForDisplay(this.summaryResults + res.Response);
          this.resetAISummaryFailureState();
          this.summarisedSinceLastSearch = true;
        }
        this.prompt = '';
      }),
      finalize(() => this.completeAISummaryRequest())
    ).subscribe({
      error: () => {
        this.lastFailedFollowUpPrompt = question;
        this.applyAISummaryFailure(new AuditSummaryResult({
          IsError: true,
          ErrorCode: 'AI_TIMEOUT',
          ErrorMessage: 'The summary took too long to complete. Please try again.',
          Retryable: true
        }), true, 'followup');
        this.prompt = '';
      }
    });
  }

  isBlankInitialAISummaryResponse(response: AuditSummaryResult): boolean {
    return (!response.Response || response.Response.trim() == '')
      && (!response.IsError || response.ErrorCode == 'AIR002');
  }

  completeAISummaryRequest() {
    this.isSummarising = false;
    this.aiSummaryStatusMessage = "";
    this.aiSummaryHasStreamedText = false;
    this.showAITips = false;
    this.showAISummary = true;
    this.showList = false;
    this.showResults = false;
  }

  retryAIRequest() {
    if (this.aiSummaryRetryCountdown > 0) return;
    const retryMode = this.aiSummaryRetryMode;
    const followUpPrompt = this.lastFailedFollowUpPrompt;
    this.resetAISummaryFailureState();
    this.summarisedSinceLastSearch = false;

    if (retryMode == 'followup' && followUpPrompt) {
      this.generateFollowUp(followUpPrompt, false);
      return;
    }

    if (retryMode == 'quick-summary') {
      this.generateQuickSummary();
      return;
    }

    this.generateSummary();
  }

  startNewAIChat() {
    this.summaryResults = '';
    this.hasAISummaryResponse = false;
    this.rawSummaryResult = new AuditSummaryResult();
    this.prompt = '';
    this.aiSummaryMode = '';
    this.aiSummaryStatusMessage = '';
    this.aiSummaryHasStreamedText = false;
    this.quickSummaryContextForDeepDive = '';
    this.summarisedSinceLastSearch = false;
    this.resetAISummaryFailureState();
    this.generateQuickSummary();
  }

  applyAISummaryFailure(result: AuditSummaryResult, append: boolean, retryMode: 'summary' | 'quick-summary' | 'followup') {
    const message = this.getAISummaryFailureMessage(result);
    this.summaryResults = append ? this.summaryResults + message : message;
    this.summarisedSinceLastSearch = false;

    const contextExpired = result.ErrorCode == 'AI_CONTEXT_EXPIRED';
    this.aiSummaryStartNewChatAvailable = contextExpired;
    this.aiSummaryRetryAvailable = !!result.Retryable && !contextExpired;
    this.aiSummaryRetryMode = this.aiSummaryRetryAvailable ? retryMode : '';
    this.aiSummaryRetryButtonLabel = retryMode == 'followup' ? 'Retry question' : retryMode == 'quick-summary' ? 'Retry quick summary' : 'Retry deep dive';
    this.setAISummaryRetryCountdown(result.RetryAfterSeconds || 0);

    if (!append || contextExpired) {
      this.hasAISummaryResponse = false;
    }
  }

  resetAISummaryFailureState() {
    this.aiSummaryRetryAvailable = false;
    this.aiSummaryStartNewChatAvailable = false;
    this.aiSummaryRetryMode = '';
    this.aiSummaryRetryButtonLabel = 'Retry AI Summary';
    this.setAISummaryRetryCountdown(0);
  }

  setAISummaryRetryCountdown(seconds: number) {
    if (this.aiSummaryRetryTimer) {
      clearInterval(this.aiSummaryRetryTimer);
      this.aiSummaryRetryTimer = null;
    }

    this.aiSummaryRetryCountdown = Math.max(0, seconds);
    if (this.aiSummaryRetryCountdown == 0) return;

    this.aiSummaryRetryTimer = setInterval(() => {
      this.aiSummaryRetryCountdown = Math.max(0, this.aiSummaryRetryCountdown - 1);
      if (this.aiSummaryRetryCountdown == 0 && this.aiSummaryRetryTimer) {
        clearInterval(this.aiSummaryRetryTimer);
        this.aiSummaryRetryTimer = null;
      }
    }, 1000);
  }

  getAISummaryFailureMessage(result?: AuditSummaryResult): string {
    const message = this.customerContext.displayText(result?.ErrorMessage || 'AI Summary response failed. Please try again, or tighten the filters by reducing the date range, location selection, or number of audits.');
    const supportReference = result?.SupportReference
      ? '<p class="ai-summary-support-reference">Support reference: ' + this.escapeHTML(result.SupportReference) + '</p>'
      : '';
    return '<div class="ai-summary-failure"><p class="ai-content">' + this.escapeHTML(message) + '</p>' + supportReference + '</div>';
  }

  cleanAIResponseForDisplay(response: string): string {
    return (response || '')
      .replace(/\uE200filecite[\s\S]*?\uE201/g, '')
      .replace(/\uE200filecite[\s\S]*$/g, '');
  }

  escapeHTML(input: string): string {
    const element = document.createElement('div');
    element.innerText = input || '';
    return element.innerHTML;
  }
  exportAllAuditResults() {
    this.isExporting = true;
    this.exportDialogTitle = "Exporting full details";
    this.exportDialogMessage = "Preparing the full details workbook. This can take a few minutes for larger exports.";
    this.exportStatus = "Starting export...";
    this.exportProgress = 0;
    this.exportProgressMode = 'determinate';
    this.exportService.startFullAuditDetailReport(this.currentRequest).pipe(tap((res: ExportJobResult) => {
      if (res.IsError || !res.JobID) {
        this.resetExportState();
        this.toastr.addToastr(new Toastr({ Type: 3, Message: "Unable to start export, please try again." }));
        return;
      }
      this.pollFullAuditExport(res.JobID);
    })).subscribe({
      error: () => {
        this.resetExportState();
        this.toastr.addToastr(new Toastr({ Type: 3, Message: "Unable to start export, please try again." }));
      }
    });

  }
  pollFullAuditExport(jobID: string) {
    this.exportService.getFullAuditDetailReportStatus(jobID).pipe(tap((res: ExportJobResult) => {
      if (res.IsError) {
        this.resetExportState();
        this.toastr.addToastr(new Toastr({ Type: 3, Message: res.ErrorMessage || "Unable to export results, please try again." }));
        return;
      }
      this.exportProgress = res.Progress;
      this.exportStatus = res.Status;
      if (res.IsComplete) {
        this.exportService.downloadFullAuditDetailReport(jobID).pipe(tap(() => {
          this.resetExportState();
        })).subscribe({
          error: () => {
            this.resetExportState();
            this.toastr.addToastr(new Toastr({ Type: 3, Message: "Unable to download export, please try again." }));
          }
        });
        return;
      }
      setTimeout(() => this.pollFullAuditExport(jobID), 2000);
    })).subscribe({
      error: () => {
        this.resetExportState();
        this.toastr.addToastr(new Toastr({ Type: 3, Message: "Unable to check export progress, please try again." }));
      }
    });
  }
  startEstimatedExportProgress(auditCount: number) {
    this.clearExportEstimateTimer();
    const estimatedSeconds = this.getEstimatedExportSeconds(auditCount);
    const estimatedMilliseconds = estimatedSeconds * 1000;
    const startedAt = Date.now();

    this.exportEstimateTimer = setInterval(() => {
      const elapsedMilliseconds = Date.now() - startedAt;
      const estimatedProgress = Math.floor((elapsedMilliseconds / estimatedMilliseconds) * 95);
      this.exportProgress = Math.min(95, Math.max(1, estimatedProgress));

      const remainingSeconds = Math.max(0, Math.ceil((estimatedMilliseconds - elapsedMilliseconds) / 1000));
      this.exportStatus = `Around ${this.formatDuration(remainingSeconds)} remaining.`;
    }, 1000);
  }
  getEstimatedExportSeconds(auditCount: number): number {
    const safeAuditCount = Math.max(auditCount, 1);
    if (safeAuditCount <= 6000) {
      return Math.max(10, 5 + (safeAuditCount / 195));
    }
    if (safeAuditCount <= 8000) {
      return Math.max(40, 8 + (safeAuditCount / 115));
    }
    return Math.max(80, 8 + (safeAuditCount / 65));
  }
  clearExportEstimateTimer() {
    if (this.exportEstimateTimer) {
      clearInterval(this.exportEstimateTimer);
      this.exportEstimateTimer = null;
    }
  }
  formatDuration(totalSeconds: number): string {
    if (totalSeconds < 60) {
      return `${totalSeconds} seconds`;
    }
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (seconds == 0) {
      return `${minutes} minutes`;
    }
    return `${minutes} minutes ${seconds} seconds`;
  }
  resetExportState() {
    this.clearExportEstimateTimer();
    this.isExporting = false;
    this.exportDialogTitle = "";
    this.exportDialogMessage = "";
    this.exportStatus = "";
    this.exportProgress = 0;
    this.exportProgressMode = 'indeterminate';
  }
  switchAdditionalOptions() {
    this.showAdditionalOptions = !this.showAdditionalOptions;
  }
  removeStartDateTimeZone(event: Date) {
    const localDate = this.utilService.removeTimezoneFromDate(event)
    this.startDate = localDate;
  }
  removeEndDateTimeZone(event: Date) {
    const localDate2 = this.utilService.removeTimezoneFromDate(event);
    this.endDate = localDate2;
  }
  returnEndDateAdjust(event: Date): Date {
    this.removeEndDateTimeZone(event);
    var returnDate: Date = new Date(this.endDate);
    returnDate.setDate(returnDate.getDate() + 1);
    return returnDate;
  }
  getAuditTypeName(TypeID: number) {
    return this.auditTypes.find(e => { return e.ID == TypeID })?.Description;
  }
  getAuditFrequencyName(FreqID: number) {
    return this.frequencies.find(e => { return e.ID == FreqID })?.Description;
  }
  switchAITips() {
    this.showAITips = !this.showAITips;
  }
  changeHelp() {
    this.showKey = !this.showKey;
  }
  switchToResult(c: Campaign) {
    if (c.TotalSubmitted == 0) {
      return;
    }
    this.campaignResultLoading = true;
    this.fetchCampaignResults(c.AuditRequestID);


  }
  fetchCampaignResults(campaignResultID: number) {
    this.auditRequestService.getCampaignResult(campaignResultID).pipe(tap((res: CompletedCampaignResult) => {
      if (!res.IsError) {
        this.showList = false;
        this.completedCampaign = new CompletedCampaignResult(res);
        this.campaignAuditResultView = true;
      } else {

      }
      this.campaignResultLoading = false;

    })).subscribe();

  }
  getCampaignAuditResult(campaignAuditResultID: number) {
    this.campaignResultLoading = true;
    this.auditRequestService.getCampaignResultDetails(campaignAuditResultID).pipe(tap((res: CompletedCampaignResult) => {
      if (!res.IsError) {
        this.showList = false;
        this.campaignResultDetail = new CompletedCampaignResult(res);
        this.campaignAuditDetailView = true;
        this.campaignAuditResultView = false;
      } else {
        //show error
      }
      this.campaignResultLoading = false;

    })).subscribe();
  }





  //actions print section

  printActionsPanel() {
    window.print();
    // 1) Inject a temporary style: force landscape AND remove width caps in print
    /*const style = document.createElement('style');
    style.setAttribute('data-temp-print-style', 'true');
    style.textContent = `
      @page { size: A4 landscape; margin: 12mm; }
      @media print {
        html, body { width: 100% !important; margin: 0 !important; }
        #actionsPanel,
        .container,
        .mat-sidenav-content,
        .mat-typography,
        .content,
        .wrapper {
          max-width: none !important;
          width: auto !important;
        }
        #actionsPanel table { width: 100% !important; }
      }
    `;
    document.head.appendChild(style);
  
    // 2) Let the browser apply the new CSS (two RAFs is reliable), then print
    const cleanup = () => {
      style.remove();
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
  
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
      });
    });*/
  }
}
